/**
 * hooks/useWizardNavigation.ts
 * Onboarding-specific wizard navigation
 * 
 * Uses the shared wizard navigation hook with onboarding-specific configuration:
 * - Step definitions for the onboarding flow
 * - Integration with useOnboardingData
 * - Account type-based conditional steps
 */

// No external React dependencies needed

// 2. Internal absolute imports
import { useWizardNavigation as useSharedWizardNavigation } from '@/components/shared/layouts';
import { Brand } from '@enfomentis/shared/config';
import type { AccountType } from '@/types/shared/AccountType';

// 3. Relative imports
import { useOnboardingData } from './useOnboardingData';
import type { OnboardingScreenId } from '../types/screens';
import type { WizardStep, UseWizardNavigationReturn } from '@/components/shared/layouts';

// Re-export WizardStep type for use by onboarding steps
export type { WizardStep };

/**
 * Onboarding-specific draft data type
 */
interface OnboardingDraftData {
  account?: {
    accountName?: string;
    accountType?: AccountType;
    selectedAssetClasses?: string[];
    propFirmDetails?: {
      firmName?: string;
      accountStage?: string;
    };
    selectedBrokerId?: string;
    feeConfiguration?: {
      choice?: 'standard' | 'custom' | 'pending';
      isCustomBroker?: boolean;
      standardFeesAccepted?: boolean;
      configurationComplete?: boolean;
    };
  };
  trading?: {
    strategy?: {
      name?: string;
      description?: string;
      philosophy?: string;
    };
    playbooks?: Array<{
      tempId: string;
      name: string;
      description?: string;
      philosophy?: string;
      criteriaIds?: string[];
      confluences?: Array<{
        confluenceId: string;
        polarity: 'positive' | 'negative';
      }>;
      entryModelIds: string[];
    }>;
    riskConfiguration?: {
      profiles: Array<{
        tempId: string;
        name: string;
        accountDefault: {
          type: 'percentage' | 'fixed';
          value: number;
        };
      }>;
    };
  };
  personal?: Record<string, unknown>;
  completedScreens?: OnboardingScreenId[];
}

/**
 * Default onboarding wizard step configuration
 */
const ONBOARDING_STEPS: WizardStep<OnboardingDraftData>[] = [
  {
    id: 'welcome',
    name: 'Welcome',
    title: `Welcome to ${Brand.displayName}`,
    description: 'Let\'s set up your perfect trading journal',
    excludeFromProgress: true, // Welcome screen doesn't count toward setup progress
    validation: (draft?: OnboardingDraftData) => {
      // Check if welcome has been completed (marked as read)
      return draft?.completedScreens?.includes('welcome') ?? false;
    }
  },
  {
    id: 'storage-provider',
    name: 'Storage Provider',
    title: 'Choose Your Storage',
    description: 'Select where to store your trading data',
    // TODO: Replace with real validation when StorageProviderStep is fully implemented (Task 2-4)
    validation: () => true
  },
  {
    id: 'account-intro',
    name: 'Intro to Accounts, Assets, and Brokers',
    title: 'Intro to Accounts, Assets, and Brokers',
    description: 'Learn about account configuration',
    excludeFromProgress: true, // Educational screen doesn't count toward setup progress
    validation: (draft?: OnboardingDraftData) => {
      // Check if account intro has been completed (marked as read)
      return draft?.completedScreens?.includes('account-intro') ?? false;
    }
  },
  {
    id: 'account-details',
    name: 'Account Details',
    title: 'Account Details',
    description: 'Basic information about your trading account',
    validation: (draft?: OnboardingDraftData) => !!draft?.account?.accountName?.trim()
  },
  {
    id: 'account-type',
    name: 'Account Type',
    title: 'Account Type',
    description: 'Personal or prop firm account?',
    validation: (draft?: OnboardingDraftData) => !!draft?.account?.accountType
  },
  {
    id: 'assets',
    name: 'Assets',
    title: 'Assets Traded',
    description: 'What assets do you trade?',
    validation: (draft?: OnboardingDraftData) => (draft?.account?.selectedAssetClasses?.length || 0) > 0
  },
  {
    id: 'prop-firm',
    name: 'Prop Firm',
    title: 'Prop Firm Setup',
    description: 'Configure your prop firm details',
    condition: (draft?: OnboardingDraftData) => draft?.account?.accountType === 'prop',
    validation: (draft?: OnboardingDraftData) => {
      if (draft?.account?.accountType !== 'prop') return true;
      return !!draft?.account?.propFirmDetails?.firmName && !!draft?.account?.propFirmDetails?.accountStage;
    }
  },
  {
    id: 'broker',
    name: 'Broker',
    title: 'Select Your Broker',
    description: 'Choose your trading broker',
    validation: (draft?: OnboardingDraftData) => !!draft?.account?.selectedBrokerId
  },
  {
    id: 'fee-config-choice',
    name: 'Fee Configuration',
    title: 'Configure Broker Fees',
    description: 'Set up your broker fee structure',
    condition: (draft?: OnboardingDraftData) => {
      const brokerId = draft?.account?.selectedBrokerId;

      // Must have broker selected
      if (!brokerId) return false;

      // Always show choice step for non-custom brokers
      // User should always be able to change their choice when returning to this step
      return brokerId !== 'custom-broker';
    },
    validation: (draft?: OnboardingDraftData) => {
      const feeConfig = draft?.account?.feeConfiguration;
      return feeConfig?.choice !== 'pending' && feeConfig?.choice !== undefined;
    }
  },
  {
    id: 'fee-config-detail',
    name: 'Fee Details',
    title: 'Customize Fee Structure',
    description: 'Configure detailed fee settings',
    condition: (draft?: OnboardingDraftData) => {
      const feeConfig = draft?.account?.feeConfiguration;
      const brokerId = draft?.account?.selectedBrokerId;

      // Must have broker selected
      if (!brokerId) return false;

      // Show for custom brokers OR when user chose custom fees for standard broker
      return brokerId === 'custom-broker' || feeConfig?.choice === 'custom';
    },
    validation: (draft?: OnboardingDraftData) => {
      return draft?.account?.feeConfiguration?.configurationComplete === true;
    }
  },
  {
    id: 'strategy-intro',
    name: 'Strategy Introduction',
    title: 'Understanding Strategies',
    description: 'Learn how strategies work in your trading journal',
    excludeFromProgress: true, // Educational screen doesn't count toward setup progress
    validation: (draft?: OnboardingDraftData) => {
      // Check if strategy introduction has been completed (marked as read)
      return draft?.completedScreens?.includes('strategy-intro') ?? false;
    }
  },
  {
    id: 'strategy-definition',
    name: 'Strategy Definition',
    title: 'Define Your Strategy',
    description: 'Create your trading strategy',
    validation: (draft?: OnboardingDraftData) => !!draft?.trading?.strategy?.name?.trim()
  },
  {
    id: 'playbook-builder',
    name: 'Playbook Builder',
    title: 'Build Your Playbooks',
    description: 'Create playbooks for your trading strategy',
    validation: (draft?: OnboardingDraftData) => {
      const playbooks = draft?.trading?.playbooks;

      // Must have at least one playbook
      if (!playbooks || playbooks.length === 0) return false;

      // Each playbook must have a name and at least one entry model
      return playbooks.every(playbook =>
        playbook.name?.trim() &&
        playbook.entryModelIds?.length > 0
      );
    }
  },
  {
    id: 'risk-intro',
    name: 'Risk Introduction',
    title: 'Understanding Risk Management',
    description: 'Learn how risk management works in your trading journal',
    excludeFromProgress: true, // Educational screen doesn't count toward setup progress
    validation: (draft?: OnboardingDraftData) => {
      // Check if risk introduction has been completed (marked as read)
      return draft?.completedScreens?.includes('risk-intro') ?? false;
    }
  },
  {
    id: 'risk-configuration',
    name: 'Risk Configuration',
    title: 'Configure Risk Profiles',
    description: 'Set up your risk management profiles',
    validation: (draft?: OnboardingDraftData) => {
      const profiles = draft?.trading?.riskConfiguration?.profiles;
      // Must have at least one profile with valid account default risk
      return (profiles?.length || 0) > 0 && (profiles?.every(p =>
        p.name && p.accountDefault?.type && p.accountDefault?.value > 0
      ) ?? false);
    }
  },
  {
    id: 'workflow-intro',
    name: 'Workflow Introduction',
    title: 'Your Trading Day Workflow',
    description: 'Learn about the three-phase trading day',
    excludeFromProgress: true, // Educational screen doesn't count toward setup progress
    validation: (draft?: OnboardingDraftData) => {
      // Check if workflow introduction has been completed (marked as read)
      return draft?.completedScreens?.includes('workflow-intro') ?? false;
    }
  },
  {
    id: 'basic-settings',
    name: 'Basic Settings',
    title: 'Configure Basic Settings',
    description: 'Set your timezone, time format, and trading schedule',
    validation: (draft?: OnboardingDraftData) => {
      const personal = draft?.personal;
      // Require timezone, timeFormat, and weekStartsOn
      return !!(
        personal?.['displayTimezone'] &&
        personal?.['timeFormat'] &&
        personal?.['weekStartsOn']
      );
    }
  },
  {
    id: 'review',
    name: 'Review & Complete',
    title: 'Review Your Configuration',
    description: 'Review and complete your onboarding setup',
    validation: (draft?: OnboardingDraftData) => {
      // Review screen is valid if all required data is present
      // Check account data
      const hasAccount = !!(
        draft?.account?.accountName &&
        draft?.account?.accountType
      );

      // Check trading data (strategy and at least one playbook)
      const hasStrategy = !!draft?.trading?.strategy?.name;
      const hasPlaybooks = (draft?.trading?.playbooks?.length ?? 0) > 0;

      // Check risk configuration (at least one profile)
      const hasRiskProfiles = (draft?.trading?.riskConfiguration?.profiles?.length ?? 0) > 0;

      // Check basic settings
      const hasBasicSettings = !!(
        draft?.personal?.['displayTimezone'] &&
        draft?.personal?.['timeFormat'] &&
        draft?.personal?.['weekStartsOn']
      );

      return hasAccount && hasStrategy && hasPlaybooks && hasRiskProfiles && hasBasicSettings;
    }
  }
];

/**
 * Onboarding-specific wizard navigation hook
 *
 * @param steps - Optional custom step configuration (defaults to ONBOARDING_STEPS)
 * @param initialStepIndex - Optional initial step index (defaults to 0)
 * @returns Wizard navigation state and methods
 */
export const useWizardNavigation = (
  steps: WizardStep<OnboardingDraftData>[] = ONBOARDING_STEPS,
  initialStepIndex?: number
): UseWizardNavigationReturn<OnboardingDraftData> => {
  const { draft } = useOnboardingData();


  // Use the shared wizard navigation hook with onboarding draft data
  return useSharedWizardNavigation({
    steps,
    data: draft as OnboardingDraftData,
    initialStepIndex: initialStepIndex ?? 0
  });
};